import { cn } from '@/lib/cn';

export type RobotProgressItem = {
  robot_id: number;
  robot_name: string;
  moments_done: number;
  groups_done: number;
  moments_planned: number;
  groups_planned: number;
  completed?: boolean;
  /** 未到发圈频次间隔时，服务端不下发 mp_android_tasks */
  moments_pull_wait?: string | null;
  /** 未到群发频次间隔时，服务端不下发 mp_android_tasks */
  groups_pull_wait?: string | null;
};

export type ProgressAdjustChannel = 'moments' | 'groups';

function normalizeChannel(v?: string): 'moments' | 'groups' | 'all' {
  if (v === 'moments' || v === 'groups' || v === 'all') return v;
  return 'all';
}

function AdjustButton({
  label,
  disabled,
  busy,
  onClick,
}: {
  label: string;
  disabled: boolean;
  busy: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled || busy}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={cn(
        'inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded border px-1 font-mono text-[10px] font-semibold leading-none transition-colors',
        disabled || busy
          ? 'cursor-not-allowed border-slate-100 bg-slate-50 text-slate-300'
          : 'border-slate-200 bg-white text-slate-600 hover:border-teal-300 hover:bg-teal-50 hover:text-teal-800',
      )}
    >
      {busy ? '…' : label === '减 1 天' ? '−' : '+'}
    </button>
  );
}

function ProgressMetric({
  label,
  done,
  planned,
  tone,
  channel,
  robotId,
  editable,
  busy,
  cooldownResetBusy,
  pullWait,
  onAdjust,
  onResetCooldown,
}: {
  label: string;
  done: number;
  planned: number;
  tone: 'moments' | 'groups';
  channel: ProgressAdjustChannel;
  robotId: number;
  editable: boolean;
  busy: boolean;
  cooldownResetBusy: boolean;
  pullWait?: string | null;
  onAdjust?: (robotId: number, channel: ProgressAdjustChannel, delta: 1 | -1) => void;
  onResetCooldown?: (robotId: number, channel: ProgressAdjustChannel) => void;
}) {
  if (planned <= 0) return null;
  const finished = done >= planned;
  const pct = planned > 0 ? Math.min(100, Math.round((done / planned) * 100)) : 0;
  const barColor = tone === 'moments' ? 'bg-sky-500' : 'bg-violet-500';
  const textColor = finished
    ? 'text-emerald-700'
    : tone === 'moments'
      ? 'text-sky-800'
      : 'text-violet-800';
  const canDec = done > 0;
  const canInc = done < planned;

  return (
    <div className="min-w-[112px]">
      <div className="flex items-center justify-between gap-1">
        <span className="text-[10px] text-slate-500">{label}</span>
        <div className="flex items-center gap-0.5">
          {editable && onAdjust ? (
            <AdjustButton
              label="减 1 天"
              disabled={!canDec}
              busy={busy}
              onClick={() => onAdjust(robotId, channel, -1)}
            />
          ) : null}
          <span className={cn('min-w-[2.25rem] text-center font-mono text-[11px] font-semibold tabular-nums', textColor)}>
            {done}/{planned}
          </span>
          {editable && onAdjust ? (
            <AdjustButton
              label="加 1 天"
              disabled={!canInc}
              busy={busy}
              onClick={() => onAdjust(robotId, channel, 1)}
            />
          ) : null}
        </div>
      </div>
      <div className="mt-0.5 h-1 overflow-hidden rounded-full bg-slate-100">
        <div
          className={cn('h-full rounded-full transition-all', finished ? 'bg-emerald-500' : barColor)}
          style={{ width: `${pct}%` }}
        />
      </div>
      {pullWait && !finished ? (
        <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
          <p className="text-[10px] leading-snug text-amber-800" title="机器人拉取任务间隔">
            冷却：{pullWait}
          </p>
          {editable && onResetCooldown ? (
            <button
              type="button"
              disabled={cooldownResetBusy || busy}
              title="跳过本次冷却，机器人下次拉取时可立即执行；执行后仍按原频次计算下一次间隔"
              onClick={(e) => {
                e.stopPropagation();
                onResetCooldown(robotId, channel);
              }}
              className={cn(
                'inline-flex items-center rounded border px-1.5 py-px text-[10px] font-medium leading-tight transition-colors',
                cooldownResetBusy || busy
                  ? 'cursor-not-allowed border-slate-100 bg-slate-50 text-slate-300'
                  : 'border-amber-200 bg-amber-50 text-amber-900 hover:border-amber-300 hover:bg-amber-100',
              )}
            >
              {cooldownResetBusy ? '处理中…' : '跳过冷却'}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function RobotProgressRow({
  item,
  channel,
  editable,
  adjustBusyKey,
  cooldownResetBusyKey,
  onAdjust,
  onResetCooldown,
}: {
  item: RobotProgressItem;
  channel: 'moments' | 'groups' | 'all';
  editable: boolean;
  adjustBusyKey: string;
  cooldownResetBusyKey: string;
  onAdjust?: (robotId: number, channel: ProgressAdjustChannel, delta: 1 | -1) => void;
  onResetCooldown?: (robotId: number, channel: ProgressAdjustChannel) => void;
}) {
  const name = item.robot_name || `机器人#${item.robot_id}`;
  const completed = item.completed === true;
  const showMoments = channel === 'moments' || channel === 'all';
  const showGroups = channel === 'groups' || channel === 'all';

  return (
    <div
      className={cn(
        'rounded-lg border px-2.5 py-2',
        completed ? 'border-emerald-200/80 bg-emerald-50/40' : 'border-slate-200/90 bg-white',
      )}
    >
      <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
        <span className={cn('text-xs font-semibold', completed ? 'text-emerald-900' : 'text-slate-800')}>
          {name}
        </span>
        {completed ? (
          <span className="rounded-full bg-emerald-100 px-1.5 py-px text-[10px] font-medium text-emerald-800 ring-1 ring-emerald-200/80">
            已完成
          </span>
        ) : null}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1.5">
        {showMoments ? (
          <ProgressMetric
            label="发圈"
            done={item.moments_done}
            planned={item.moments_planned}
            tone="moments"
            channel="moments"
            robotId={item.robot_id}
            editable={editable}
            busy={adjustBusyKey === `${item.robot_id}:moments`}
            cooldownResetBusy={cooldownResetBusyKey === `${item.robot_id}:moments`}
            pullWait={item.moments_pull_wait}
            onAdjust={onAdjust}
            onResetCooldown={onResetCooldown}
          />
        ) : null}
        {showGroups ? (
          <ProgressMetric
            label="群发"
            done={item.groups_done}
            planned={item.groups_planned}
            tone="groups"
            channel="groups"
            robotId={item.robot_id}
            editable={editable}
            busy={adjustBusyKey === `${item.robot_id}:groups`}
            cooldownResetBusy={cooldownResetBusyKey === `${item.robot_id}:groups`}
            pullWait={item.groups_pull_wait}
            onAdjust={onAdjust}
            onResetCooldown={onResetCooldown}
          />
        ) : null}
      </div>
    </div>
  );
}

export function MicroPromotionRobotProgressCell({
  items,
  promotionChannel,
  editable = false,
  adjustBusyKey = '',
  cooldownResetBusyKey = '',
  onAdjust,
  onResetCooldown,
}: {
  items?: RobotProgressItem[];
  promotionChannel?: string;
  editable?: boolean;
  adjustBusyKey?: string;
  cooldownResetBusyKey?: string;
  onAdjust?: (robotId: number, channel: ProgressAdjustChannel, delta: 1 | -1) => void;
  onResetCooldown?: (robotId: number, channel: ProgressAdjustChannel) => void;
}) {
  const list = Array.isArray(items) ? items : [];
  const channel = normalizeChannel(promotionChannel);

  if (list.length === 0) {
    return (
      <span className="text-xs text-slate-400" title="请先在「推广区域 → 绑定机器人」中配置执行机器人">
        未绑定机器人
      </span>
    );
  }

  return (
    <div className="flex min-w-[200px] max-w-[300px] flex-col gap-1.5">
      {list.map((item) => (
        <RobotProgressRow
          key={item.robot_id}
          item={item}
          channel={channel}
          editable={editable}
          adjustBusyKey={adjustBusyKey}
          cooldownResetBusyKey={cooldownResetBusyKey}
          onAdjust={onAdjust}
          onResetCooldown={onResetCooldown}
        />
      ))}
    </div>
  );
}
